import { runInAction } from "mobx";
import type {
  IWarehouseApi,
  ProductExpiryFilter,
  ProductPlacementFilter,
} from "../../../api/WarehouseApi";
import { isBoxId, isProductId } from "../../../domain/entity-ids";
import type { Product } from "../../../stores/Product";
import { type INavigationStore, Screens } from "../../../stores/navigation";
import type { IStore } from "../../../stores/store";
import { Measures } from "../../../types/entities";
import { formatTypeTag, normalizeTypeTag } from "../../../utils/typeTags";
import {
  openBoxScreenById,
  openProductScreenById,
} from "../../shared/use-cases/entity-navigation";

export interface ProductsListNavigationState {
  error: string | null;
}

function setNavigationError(
  state: ProductsListNavigationState,
  message: string,
): void {
  runInAction(() => {
    state.error = message;
  });
}

export function openProductCard(input: {
  product: Product;
  store: IStore;
  navigationStore: INavigationStore;
}): void {
  const existingProduct = input.store.products.find(
    (item) => item.id === input.product.id,
  );

  if (existingProduct) {
    input.navigationStore.setScreen(Screens.Product, existingProduct);
    return;
  }

  input.store.addProduct(input.product);
  input.navigationStore.setScreen(Screens.Product, input.product);
}

export function resolveProductLocation(input: {
  product: Product;
  store: IStore;
}): string {
  const parentId = input.product.parentId?.trim() || "";
  if (!parentId) {
    return "Без коробки";
  }

  const fromLoaded = input.store.boxes.find((box) => box.id === parentId);
  if (fromLoaded?.name?.trim()) {
    return `В коробке: ${fromLoaded.name}`;
  }

  return `Коробка: ${parentId}`;
}

export function openTypeTagFilter(input: {
  tag: string;
  navigationStore: INavigationStore;
}): void {
  const normalizedTag = normalizeTypeTag(input.tag || "");
  if (!normalizedTag) {
    return;
  }

  input.navigationStore.setScreen(Screens.ProductsList, {
    title: `Товары по тегу ${formatTypeTag(normalizedTag)}`,
    typeTag: normalizedTag,
  });
}

export function openMeasureFilter(input: {
  measure: Measures;
  navigationStore: INavigationStore;
}): void {
  input.navigationStore.setScreen(Screens.ProductsList, {
    title:
      input.measure === Measures.weight
        ? "Товары по мере: вес"
        : input.measure === Measures.liters
          ? "Товары по мере: литры"
          : "Товары по мере: количество",
    measure: input.measure,
  });
}

export function openPlacementFilter(input: {
  placement: ProductPlacementFilter;
  navigationStore: INavigationStore;
  openAllProductsList: () => void;
}): void {
  if (input.placement === "withoutBox") {
    input.navigationStore.setScreen(Screens.ProductsList, {
      title: "Товары без коробки",
      placement: "withoutBox",
    });
    return;
  }

  if (input.placement === "inBox") {
    input.navigationStore.setScreen(Screens.ProductsList, {
      title: "Товары в коробках",
      placement: "inBox",
    });
    return;
  }

  input.openAllProductsList();
}

export function openExpiryFilter(input: {
  expiry: ProductExpiryFilter;
  expiringDays: number;
  navigationStore: INavigationStore;
  openAllProductsList: () => void;
}): void {
  if (input.expiry === "expired") {
    input.navigationStore.setScreen(Screens.ProductsList, {
      title: "Просроченные товары",
      expiry: "expired",
    });
    return;
  }

  if (input.expiry === "expiringSoon") {
    input.navigationStore.setScreen(Screens.ProductsList, {
      title: `Товары с истекающим сроком (<= ${input.expiringDays} дн.)`,
      expiry: "expiringSoon",
      expiringDays: input.expiringDays,
    });
    return;
  }

  input.openAllProductsList();
}

export function openQueryFilter(input: {
  query: string;
  navigationStore: INavigationStore;
  openAllProductsList: () => void;
}): void {
  const normalizedQuery = input.query.trim();
  if (!normalizedQuery) {
    input.openAllProductsList();
    return;
  }

  input.navigationStore.setScreen(Screens.ProductsList, {
    title: `Поиск: ${normalizedQuery}`,
    q: normalizedQuery,
  });
}

export function openAllProductsList(navigationStore: INavigationStore): void {
  navigationStore.setScreen(Screens.ProductsList, {
    title: "Список товаров",
  });
}

export async function openLocationFilter(input: {
  state: ProductsListNavigationState;
  product: Product;
  api: IWarehouseApi;
  store: IStore;
  navigationStore: INavigationStore;
}): Promise<void> {
  const parentId = input.product.parentId?.trim() || "";

  if (isBoxId(parentId)) {
    const existingBox = input.store.boxes.find((item) => item.id === parentId);
    if (existingBox) {
      input.navigationStore.setScreen(Screens.Box, existingBox);
      return;
    }

    await openBoxScreenById({
      api: input.api,
      navigationStore: input.navigationStore,
      boxId: parentId,
      onLoaded: (box) => {
        input.store.addBox(box);
      },
      onNotFound: () => {
        setNavigationError(input.state, "Коробка не найдена");
      },
      onError: (error) => {
        setNavigationError(
          input.state,
          error instanceof Error ? error.message : "Не удалось открыть коробку",
        );
      },
    });
    return;
  }

  input.navigationStore.setScreen(Screens.ProductsList, {
    title: "Товары без коробки",
    placement: "withoutBox",
  });
}

export async function openReferenceId(input: {
  state: ProductsListNavigationState;
  id: string;
  api: IWarehouseApi;
  store: IStore;
  navigationStore: INavigationStore;
}): Promise<void> {
  const normalizedId = input.id.trim();
  if (!normalizedId) {
    return;
  }

  if (isProductId(normalizedId)) {
    const existingProduct = input.store.products.find(
      (item) => item.id === normalizedId,
    );
    if (existingProduct) {
      input.navigationStore.setScreen(Screens.Product, existingProduct);
      return;
    }

    await openProductScreenById({
      api: input.api,
      navigationStore: input.navigationStore,
      productId: normalizedId,
      onLoaded: (product) => {
        input.store.addProduct(product);
      },
      onNotFound: () => {
        setNavigationError(input.state, "Товар не найден");
      },
      onError: (error) => {
        setNavigationError(
          input.state,
          error instanceof Error ? error.message : "Не удалось открыть товар",
        );
      },
    });
    return;
  }

  if (isBoxId(normalizedId)) {
    const existingBox = input.store.boxes.find(
      (item) => item.id === normalizedId,
    );
    if (existingBox) {
      input.navigationStore.setScreen(Screens.Box, existingBox);
      return;
    }

    await openBoxScreenById({
      api: input.api,
      navigationStore: input.navigationStore,
      boxId: normalizedId,
      onLoaded: (box) => {
        input.store.addBox(box);
      },
      onNotFound: () => {
        setNavigationError(input.state, "Коробка не найдена");
      },
      onError: (error) => {
        setNavigationError(
          input.state,
          error instanceof Error ? error.message : "Не удалось открыть коробку",
        );
      },
    });
  }
}
